<template>
  <v-layout class="subscription-email-page">
    <v-layout wrap class="form-wrapper">
      <v-layout wrap>
        <v-flex xs12>
          <form v-scroll-to-error @submit.prevent="submitForm">
            <v-flex class="title-screen">
              <span>{{ adminText('organizationAll.emails_to_subscribers_1820e838', 'Emails to Subscribers') }}</span>
            </v-flex>
            <v-flex>
              <p v-if="pendingEmailId" role="status">
                {{ adminText('organizationAll.sending_3cc1a8e5', 'Sending “') }}{{ form.subject }}{{ adminText('organizationAll.retry_continues_this_message_without_resendin_7483a95d', '”. Retry continues this message without resending confirmed deliveries.') }}
              </p>
              <create-theater-subscription-email-form
                v-if="!pendingEmailId"
                :organization-slug="organizationSlug"
                :preselected-purchase-id="preselectedPurchaseId"
                :vs="$v"
                :form="form"
                @values="form = $event"
              >
                <email-shortcodes slot="shortcodes" :shortcodes="shortcodes" @shortcode="shortcodeAdded" />
              </create-theater-subscription-email-form>
            </v-flex>
            <div class="buttons-container">
              <main-button flat type="button" @click.prevent="goBack">{{ adminText('organizationAll.cancel_1507c40f', 'CANCEL') }}</main-button>
              <div class="lower-buttons">
                <main-button depressed type="submit" :loading="sending" :disabled="sending">
                  {{ pendingEmailId ? adminText('organizationAll.retry_unsent_15aab113', 'RETRY UNSENT') : adminText('organizationAll.send_a2469c47', 'SEND') }}
                </main-button>
              </div>
            </div>
          </form>
        </v-flex>
      </v-layout>
    </v-layout>
    <error-snackbar
      :open="emailError"
      :title="adminText('organizationAll.oops_c8e633f4', 'Oops')"
      :message="pendingEmailId ? adminText('organizationAll.some_deliveries_could_not_be_confirmed_retry__02fd31b3', 'Some deliveries could not be confirmed. Retry sends only confirmed failures; uncertain deliveries need support review.') : adminText('organizationAll.an_error_occurred_while_preparing_your_email_13619c3b', 'An error occurred while preparing your email.')"
    />
  </v-layout>
</template>

<script>
import OrganizationLocalization from '@/components/organizations/organization-all-display'

import { email, required } from 'vuelidate/lib/validators'
import EmailShortcodes from '@/components/emails/email-shortcodes.vue'
import ErrorSnackbar from '@/components/shared/snackbars/errors-snackbar.vue'
import { GET_ORGANIZATION } from '@/graphql/organization.graphql'
import { CREATE_ORGANIZATION_EMAIL, SEND_ORGANIZATION_EMAIL } from '@/graphql/email.graphql'
import { EventBus } from '@/plugins/event-bus'
import { submitGuard } from '@/utils'
import CreateTheaterSubscriptionEmailForm from '@/components/emails/CreateTheaterSubscriptionEmailForm.vue'
import { theaterOrganizationRouteNames } from '@/routing/routeNames/theaterOrganizerRouterNames'

export default {
  mixins: [OrganizationLocalization],
  name: 'theater-subscriptions-create-email',
  components: {
    CreateTheaterSubscriptionEmailForm,
    EmailShortcodes,
    ErrorSnackbar,
  },
  props: {
    organizationSlug: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      form: {
        content: '',
        replyTo: '',
        subject: '',
        recipients: this.$route && this.$route.query && this.$route.query.recipientId ? 'Specific subscribers' : 'All subscribers',
        season: '',
        subscriptionPlan: null,
        eventId: null,
        attendees: [],
      },
      pendingEmailId: null,
      sending: false,
      emailError: false,
      shortcodes: [
        'Subscription name',
        'Subscription start date',
        'Subscription end date',
        'Price',
        'Subscriber name',
        'Subscriber email',
        'Seat',
      ],
    }
  },
  computed: {
    preselectedPurchaseId() {
      return this.$route && this.$route.query && this.$route.query.recipientId || null
    },
  },
  validations() {
    return {
      form: {
        replyTo: { required, email },
        recipients: { required },
        subject: { required },
        content: { required },
      },
    }
  },
  apollo: {
    getOrganization: {
      query: GET_ORGANIZATION,
      manual: true,
      variables() {
        return { organizationSlug: this.organizationSlug }
      },
      result({ data, loading }) {
        if (!loading && data && data.getOrganization) {
          this.form.replyTo = data.getOrganization.email
        }
      },
    },
  },
  methods: {
    goBack() {
      this.$router.push({
        name: theaterOrganizationRouteNames.theaterOrganizationTheaterSubscribersEmails,
        params: { organizationSlug: this.organizationSlug },
      })
    },
    async shortcodeAdded(shortcode) {
      if (this.form.content) {
        this.form.content = this.form.content
          .slice(0, this.form.content.length - 4)
          .concat(` { ${shortcode} }`)
      } else {
        this.form.content = this.form.content.concat(`{ ${shortcode} }`)
      }
    },
    async createNewEmail() {
      const { replyTo, recipients, subject, content, attendees } = this.form
      const identifiers = (attendees || []).map(a => String(a.id || a))

      if (!identifiers.length) {
        this.emailError = true

        return
      }

      if (!this.pendingEmailId) {
        const created = await this.$apollo.mutate({
          mutation: CREATE_ORGANIZATION_EMAIL,
          variables: {
            organizationSlug: this.organizationSlug,
            email: {
              replyTo,
              recipientType: recipients,
              subject,
              content,
              identifiers,
              meta: { theaterSubscription: true, season: this.form.season || null, subscriptionPlanId: this.form.subscriptionPlan || null, eventId: this.form.eventId || null },
            },
          },
        })

        this.pendingEmailId = created.data.createOrganizationEmail.id
      }

      const { data, errors } = await this.$apollo.mutate({
        mutation: SEND_ORGANIZATION_EMAIL,
        variables: {
          organizationSlug: this.organizationSlug,
          emailId: this.pendingEmailId,
        },
      })

      if (errors || !data || data.sendOrganizationEmail !== true) {
        this.emailError = true

        return
      }

      this.goBack()
      EventBus.$emit('toggle-snackbar', { adminOrganizationNotification: true,
        title: 'Queued',
        message: 'Your email is queued. Follow delivery progress in email history.',
      })
    },
    async submitForm() {
      this.emailError = false
      this.sending = true
      try {
        await submitGuard(this, async () => {
          await this.createNewEmail()
        })
      } catch (error) {
        this.emailError = true
      } finally {
        this.sending = false
      }
    },
  },
}
</script>

<style scoped>
.subscription-email-page {
  max-width: 1038px;
  margin: 0;
}
.form-wrapper {
  flex: 1 1 100%;
  min-width: 0;
}
.form-wrapper >>> .subscription-compose-form > .flex:not(.editor-field) {
  max-width: 684px;
}
.title-screen {
  color: #ff4c00;
  font-family: Raleway, sans-serif;
  font-size: 24px;
  font-weight: 600;
  line-height: 1.2;
  letter-spacing: -0.03em;
  margin-bottom: 32px;
}
.buttons-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 32px;
  max-width: 684px;
}
</style>
